import React, {useState, useEffect} from 'react';
import {useNavigate, useParams, useLocation} from 'react-router-dom';
import Breadcrumbs from '../Components/Breadcrumbs';
import {
  CustomInput,
  CustomSelect,
  CustomToggle,
  CustomTextarea, CustomNumberInput
} from '../Components/CustomFormElements';
import Button from '../Components/Buttons';
import {AlertProvider, useAlert} from '../Components/AlertContext';
import {ConfirmationProvider, useConfirmation} from '../Components/ConfirmationContext';
import PageLoader from '../Components/PageLoader';
import {RecordRouteApiProvider, useRecordRouteApi} from "../Services/RecordRouteApiProvider";
import {isEmpty} from "../utils/validationUtil";
import RecordRouteConnectionObjectMapping from "./RecordRouteConnectionObjectMapping";

function RecordRouteDetail({canEdit}) {
  const navigate = useNavigate();
  const {organizationId, recordRouteId} = useParams();
  const {showAlert} = useAlert();

  const {
    createRecordRoute,
    updateRecordRouteById,
    getRecordRouteById,
    getChannels,
    getAgents,
    getDocuments,
    getDocumentCollections,
    getZendeskConnections,
    getCreatioConnections,
    getNotionConnections,
    triggerEventById,
    getGoogleConnections,
    getConnectionObjects,
  } = useRecordRouteApi();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isNew, setIsNew] = useState(true);
  const [isDisabled, setIsDisabled] = useState(true);

  const [isActive, setIsActive] = useState(true);
  const [routeEventType, setRouteEventType] = useState();
  const [fromType, setFromType] = useState();
  const [toType, setToType] = useState();
  const [fromKey, setFromKey] = useState();
  const [toKey, setToKey] = useState();
  const [priorityIndex, setPriorityIndex] = useState();

  const [channels, setChannels] = useState([]);
  const [assistants, setAssistants] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [documentCollections, setDocumentCollections] = useState([]);
  const [creatioConnections, setCreatioConnections] = useState([]);
  const [zendeskConnections, setZendeskConnections] = useState([]);
  const [notionConnections, setNotionConnections] = useState([]);
  const [googleConnections, setGoogleConnections] = useState([]);
  const [connectionObjects, setConnectionObjects] = useState([]);

  const [routeProperties, setRouteProperties] = useState({});
  const [fromConnectionObjectMapping, setFromConnectionObjectMapping] = useState({});
  const [toConnectionObjectMapping, setToConnectionObjectMapping] = useState({});

  useEffect(() => {
    const func = async () => {
      setLoading(true);
      getChannels().then(channels => {
        setChannels(channels ?? []);
      });
      getAgents().then(assistants => {
        setAssistants(assistants ?? []);
      });
      getDocuments().then(documents => {
        setDocuments(documents ?? []);
      });
      getDocumentCollections().then(documentCollections => {
        setDocumentCollections(documentCollections ?? []);
      });
      getCreatioConnections().then(creatioConnections => {
        setCreatioConnections(creatioConnections ?? []);
      });
      getZendeskConnections().then(zendeskConnections => {
        setZendeskConnections(zendeskConnections ?? []);
      });
      getNotionConnections().then(notionConnections => {
        setNotionConnections(notionConnections ?? []);
      });

      getGoogleConnections().then(googleConnections => {
        setGoogleConnections(googleConnections ?? []);
      });
      getConnectionObjects().then(connectionObjects => {
        setConnectionObjects(connectionObjects ?? []);
      })
      try {
        if (recordRouteId !== 'new-record-route') {
          const response = await getRecordRouteById(recordRouteId);
          setIsActive(response.is_active);
          setToType(response.to_type);
          setToKey(response.to_key);
          setFromType(response.from_type);
          setFromKey(response.from_key);
          setRouteEventType(response.route_event_type);
          setPriorityIndex(response.priority_index)

          const props = response.properties || {};
          setRouteProperties(props);

          const objectMapper = response.object_mapper || {};
          setFromConnectionObjectMapping(
            objectMapper.from_connection_object_mapping
            ?? props.from_connection_object_mapping
            ?? {}
          );
          setToConnectionObjectMapping(
            objectMapper.to_connection_object_mapping
            ?? props.to_connection_object_mapping
            ?? {}
          );
        }
      } catch (error) {
        setError('Error fetching record route data');
        console.error('Error fetching record route data:', error);
      } finally {
        setLoading(false);
      }
    }
    func();
  }, [recordRouteId]);

  useEffect(() => {
    setIsNew(recordRouteId === 'new-record-route');
    setIsDisabled(!canEdit);
  }, [recordRouteId])

  const onSave = async () => {
    if (isEmpty(routeEventType)) {
      showAlert('fail', 'Route event type is required');
      return;
    }
    if (isEmpty(fromType)) {
      showAlert('fail', 'From type is required');
      return;
    }
    if (isEmpty(toType)) {
      showAlert('fail', 'To type is required');
      return;
    }
    if (isEmpty(toKey)) {
      showAlert('fail', 'To key is required');
      return;
    }

    if (isEmpty(fromKey)) {
      showAlert('fail', 'From key is required');
      return;
    }

    const updatedProperties = routeProperties || {};

    const objectMapper = {
      from_connection_object_mapping: fromConnectionObjectMapping,
      to_connection_object_mapping: toConnectionObjectMapping,
    };

    let recordRoute = {
      route_event_type: routeEventType,
      from_key: fromKey,
      to_key: toKey,
      from_type: fromType,
      to_type: toType,
      is_active: isActive,
      properties: updatedProperties,
      object_mapper: objectMapper, // НОВЕ поле
      priority_index: priorityIndex,
    }

    setLoading(true);

    try {
      if (isNew) {
        await createRecordRoute(recordRoute);
        showAlert('success', 'Record route created successfully');
      } else {
        await updateRecordRouteById(recordRouteId, recordRoute);
        showAlert('success', 'Record route updated successfully');
      }
      navigate(`/organizations/${organizationId}/record-route`);
    } catch (error) {
      console.error('Error saving Record Route:', error);
      showAlert('fail', 'Failed to save record route');
    } finally {
      setLoading(false);
    }
  }

  const onTriggerEvent = async () => {
    setLoading(true);

    try {
      if (!isNew) {
        await triggerEventById(recordRouteId);
        showAlert('success', 'Record route triggered successfully');
      }
    } catch (error) {
      console.error('Error trigger Record Route:', error);
      showAlert('fail', 'Failed to trigger record route');
    } finally {
      setLoading(false);
    }
  }

  const onCancel = () => {
    navigate(`/organizations/${organizationId}/record-route`);
  };

  if (loading) {
    return <PageLoader/>;
  }

  const selectedFromConnectionObject =
    (connectionObjects || []).find(co => co.id === fromKey);
  const selectedToConnectionObject =
    (connectionObjects || []).find(co => co.id === toKey);

  return (
    <div className="container">
      <Breadcrumbs/>
      <div className="row d-flex gap">
        <div className="radius-yes p-2 bg-yes col-6">
          <h2 className='h2-title'>{fromType} {'->'} {toType}</h2>
          <div className='gap d-flex flex-row-to-col flex-column'>
            <div className='int-col-3 p-2 border-yes radius-yes full-width-on-sml-scr'>
              <h4 className='h4-title'>Main Settings</h4>

              <div className='input-block d-flex flex-row justify-between'>
                <div className='input-description'>
                  <div className='input-title'>Route event type</div>
                  <div className='small-font'>Select route event type</div>
                </div>
                <div className='align-wrapper d-flex align-center'>
                  <CustomSelect
                    id='route-event-type'
                    label="Route Event Type"
                    options={[
                      {value: 'manual', label: 'Manual'},
                      {value: 'channel_thread', label: 'Channel Thread'},
                      {value: 'channel_thread_message_attachment', label: 'Channel Thread Message Attachment'},
                    ]}
                    value={routeEventType}
                    onChange={(value) => {
                      setRouteEventType(value)
                    }}
                    multiSelect={false}
                    disabled={isDisabled}
                  />
                </div>
              </div>

              <div className='input-row input-block d-flex flex-row justify-between'>
                <div className='input-description'>
                  <div className='input-title'>From Type</div>
                  <div className='small-font'>Select from type</div>
                </div>
                <div className='align-wrapper d-flex align-center'>
                  <CustomSelect
                    id='from-type'
                    label="From Type"
                    options={[
                      {value: 'channel', label: 'Channel'},
                      {value: 'assistant', label: 'Assistant'},
                      {value: 'document', label: 'Document'},
                      {value: 'document_collection', label: 'Document Collection'},
                      {value: 'lead', label: 'Lead'},
                      {value: 'article', label: 'Article'},
                      {value: 'notion', label: 'Notion'},
                      {value: 'google_drive', label: 'Google Drive'},
                      {value: 'connection_object', label: 'Connection Object'},
                    ]}
                    value={fromType}
                    onChange={(value) => {
                      setFromType(value)
                    }}
                    multiSelect={false}
                    disabled={isDisabled}
                  />
                </div>
              </div>

              <div className='input-row input-block d-flex flex-row justify-between'>
                <div className='input-description'>
                  <div className='input-title'>To Type</div>
                  <div className='small-font'>Select to type</div>
                </div>
                <div className='align-wrapper d-flex align-center'>
                  <CustomSelect
                    id='to-type'
                    label="To Type"
                    options={[
                      {value: 'channel', label: 'Channel'},
                      {value: 'assistant', label: 'Assistant'},
                      {value: 'document', label: 'Document'},
                      {value: 'document_collection', label: 'Document Collection'},
                      {value: 'lead', label: 'Lead'},
                      {value: 'article', label: 'Article'},
                      {value: 'notion', label: 'Notion'},
                      {value: 'google_drive', label: 'Google Drive'},
                      {value: 'connection_object', label: 'Connection Object'},
                    ]}
                    value={toType}
                    onChange={(value) => {
                      setToType(value)
                    }}
                    multiSelect={false}
                    disabled={isDisabled}
                  />
                </div>
              </div>

              {fromType && (
                <div className="input-block d-flex flex-row justify-between">
                  <div className="input-description">
                    <div className="input-title">From key</div>
                    <div className="small-font">Select from key ({fromType})</div>
                  </div>
                  <div className="align-wrapper d-flex align-center">
                    <CustomSelect
                      id="from-key-select"
                      label="From Key"
                      options={
                        fromType === "assistant"
                          ? (assistants || []).map(a => ({value: a.id, label: a.name}))
                          : fromType === "channel"
                            ? (channels || []).map(c => ({value: c.id, label: c.name}))
                            : fromType === "document"
                              ? (documents || []).map(d => ({value: d.id, label: d.name}))
                              : fromType == "lead"
                                ? (creatioConnections || []).map(a => ({value: a.id, label: a.name}))
                                : fromType === "article"
                                  ? (zendeskConnections || []).map(a => ({value: a.id, label: a.name}))
                                  : fromType === "notion"
                                    ? (notionConnections || []).map(a => ({value: a.id, label: a.name}))
                                    : fromType === "google_drive"
                                    ? (googleConnections || []).map(a => ({value: a.id, label: a.name}))
                                    : fromType === "document_collection"
                                      ? (documentCollections || []).map(dc => ({value: dc.id, label: dc.name}))
                                    : fromType === "connection_object"
                                      ? (connectionObjects || []).map(a => ({value: a.id, label: a.name}))
                                      : fromType === "document_collection"
                                        ? (documentCollections || []).map(dc => ({value: dc.id, label: dc.name}))
                                        : []
                      }
                      value={fromKey}
                      multiSelect={false}
                      onChange={val => setFromKey(val)}
                      disabled={isDisabled}
                    />
                  </div>
                </div>
              )}

              {toType && (
                <div className="input-block d-flex flex-row justify-between">
                  <div className="input-description">
                    <div className="input-title">To key</div>
                    <div className="small-font">Select to key ({toType})</div>
                  </div>
                  <div className="align-wrapper d-flex align-center">
                    <CustomSelect
                      id="to-key-select"
                      label="To Key"
                      options={
                        toType === "assistant"
                          ? (assistants || []).map(a => ({value: a.id, label: a.name}))
                          : toType === "channel"
                            ? (channels || []).map(c => ({value: c.id, label: c.name}))
                            : toType === "document"
                              ? (documents || []).map(d => ({value: d.id, label: d.name}))
                              : toType === "lead"
                                ? (creatioConnections || []).map(a => ({value: a.id, label: a.name}))
                                : toType === "article"
                                  ? (zendeskConnections || []).map(a => ({value: a.id, label: a.name}))
                                  : toType === "notion"
                                    ? (notionConnections || []).map(a => ({value: a.id, label: a.name}))
                                    : toType === "google_drive"
                                      ? (googleConnections || []).map(a => ({value: a.id, label: a.name}))
                                    : toType === "document_collection"
                                      ? (documentCollections || []).map(dc => ({
                                        value: dc.id,
                                        label: dc.name
                                      }))
                                    : toType === "connection_object"
                                      ? (connectionObjects || []).map(a => ({value: a.id, label: a.name}))
                                      : toType === "document_collection"
                                        ? (documentCollections || []).map(dc => ({
                                          value: dc.id,
                                          label: dc.name
                                        }))
                                        : []
                      }
                      value={toKey}
                      multiSelect={false}
                      onChange={val => setToKey(val)}
                      disabled={isDisabled}
                    />
                  </div>
                </div>
              )}

              <div className="input-block d-flex flex-row justify-between">
                <div className="input-description">
                  <div className="input-title">Priority index</div>
                  <div className="small-font">
                    Lower values are executed first. Use 0 for the highest priority.
                  </div>
                </div>
                <div className="align-wrapper d-flex align-center">
                  <CustomNumberInput
                    id="priority-index"
                    label=""
                    value={priorityIndex}
                    onChange={setPriorityIndex}
                    min={0}
                    max={100}
                    step={1}
                    disabled={isDisabled}
                  />
                </div>
              </div>

              <div className='input-row input-block d-flex flex-row justify-between'>
                <div className='input-description'>
                  <div className='input-title'>Activate record route</div>
                  <div className='small-font'>Set record route to be active or inactive to enable or disable it</div>
                </div>
                <div className='align-wrapper d-flex align-center'>
                  <CustomToggle
                    id="toggle-status"
                    label={isActive ? 'Active' : 'Inactive'}
                    checked={isActive}
                    onChange={(id, value) => setIsActive(value)}
                    disabled={false}
                  />
                </div>
              </div>

              {routeEventType === 'manual' && !isNew && (
                <div className='input-row input-block d-flex flex-row justify-between'>
                  <div className='input-description'>
                    <div className='input-title'>Manual Trigger event</div>
                    <div className='small-font'>Press button to trigger event</div>
                  </div>
                  <div className='align-wrapper d-flex align-center'>
                    <Button variant='primary' size='medium' disabled={isNew} onClick={onTriggerEvent}>
                      Trigger
                    </Button>
                  </div>
                </div>
              )}

              {fromType === 'connection_object' && fromKey && selectedFromConnectionObject && (
                <RecordRouteConnectionObjectMapping
                  connectionObject={selectedFromConnectionObject}
                  mapping={fromConnectionObjectMapping}
                  onChange={setFromConnectionObjectMapping}
                  isDisabled={isDisabled}
                />
              )}

              {toType === 'connection_object' && toKey && selectedToConnectionObject && (
                <RecordRouteConnectionObjectMapping
                  connectionObject={selectedToConnectionObject}
                  mapping={toConnectionObjectMapping}
                  onChange={setToConnectionObjectMapping}
                  isDisabled={isDisabled}
                />
              )}
            </div>

            <div className='buttons-container d-flex justify-end'>
              <Button variant='primary' size='medium' disabled={isDisabled} onClick={onSave}>
                Save
              </Button>
              <Button variant='danger' size='medium' onClick={onCancel}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function RecordRoute({canEdit}) {
  return (
    <AlertProvider>
      <ConfirmationProvider>
        <RecordRouteApiProvider>
          <RecordRouteDetail canEdit={canEdit}/>
        </RecordRouteApiProvider>
      </ConfirmationProvider>
    </AlertProvider>
  );
}

export default RecordRoute;
